/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import {
	Typography,
	Divider,
	Tooltip,
	Box,
	List,
	Button,
	Skeleton,
	Alert,
	CircularProgress,
	Grid,
	IconButton,
} from "@mui/material";
import ListItemButton from "@mui/material/ListItemButton";
import "./Admin.css";
import Datagrid from "../../components/Datagrid/Datagrid";
import Settings from "../Settings/Settings";
import ManageDocuments from "../ManageDocuments/ManageDocuments";
import AdminDocumentation from "../AdminDocumentation/AdminDocumentation";
import ManageSites from "../ManageSites/ManageSites";
import { useAuth } from "../../context/authContext";
import ManageCronJobs from "../../components/ManageCronJobs/ManageCronJobs";
import ManageCustomersMaster from "../../components/ManageCustomersMaster/ManageCustomersMaster";
import ManageSitesMaster from "../../components/ManageSitesMaster/ManageSitesMaster";
import ManageProductsMaster from "../../components/ManageProductsMaster/ManageProductsMaster";
import ManageMachinesMaster from "../../components/ManageMachinesMaster/ManageMachinesMaster";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import SourceIcon from "@mui/icons-material/Source";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import StoreIcon from "@mui/icons-material/Store";
import ArticleIcon from "@mui/icons-material/Article";
import ContrastIcon from "@mui/icons-material/Contrast";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DefaultValues from "../../components/DefaultValues/DefaultValues";
import ModelUpload from "../../components/ModelUpload/ModelUpload";
import { TABLE_HEADERS } from "../../constants";
import TabTitle from "../../components/TabTitle/TabTitle";
import ManageUsers from "../../components/ManageUsers/ManageUsers";
import ManageRoles from "../../components/ManageRoles/ManageRoles";
import ManageMachineProvision from "../../components/ManageMachineProvision/ManageMachineProvision";
import NotAuthorized from "../../components/NotAuthorized/NotAuthorized";
import PriceMarkup from "../../components/PriceMarkup/PriceMarkup";
import PromotionCodes from "../../components/PromotionCodes/PromotionCodes";
import LineDiscounts from "../../components/LineDiscounts/LineDiscounts";
import ChipLearningRules from "../../components/ChipLearningRules/ChipLearningRules";
import { CHIP_REVIEW_KEY, chipReviewAlias } from "../../utilities/chipReviewAccess";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PercentIcon from '@mui/icons-material/Percent';
import { AdminPanelSettingsOutlined, ChevronLeft, ChevronRight } from "@mui/icons-material";
import GavelIcon from "@mui/icons-material/Gavel";
import CampaignIcon from "@mui/icons-material/Campaign";
import CompanyBranding from "../../components/CompanyBranding/CompanyBranding";
import { resolveEnabledAdminModules } from "../../utilities/adminModules";
import { getAllMachineModels, getAllOptions, sendPasswordResetLink, deleteUsers, insertOptions, getEndpointData, getVersionInfo } from "../../services/adminService";
import { provisionMachinesForCustomerSite } from "../../services/manageSiteMachinesService";
import HeaderTitle from "../../components/HeaderTitle/HeaderTitle";
import PolicyCenter from "../../components/PolicyCenter/PolicyCenter";
import ManageBanners from "../../components/ManageBanners/ManageBanners";
import { useLocation, useNavigate } from "react-router-dom";
import { DOCUMENT_EXTERNAL_ENABLED } from "../../config";
import ScheduleIcon from '@mui/icons-material/Schedule';
import PageNotFound from "../PageNotFound/PageNotFound";
import { useOptions } from "../../context/optionContext";
const navItems = [
	{
		header: TABLE_HEADERS.ADMINISTRATION,
		items: [
			{ key: "ITEMS_TEXT.USERS", icon: <PeopleAltIcon /> },
			{ key: "ITEMS_TEXT.ROLES", icon: <SupervisorAccountIcon /> },
			{ key: "ITEMS_TEXT.SITES", icon: <LocalLibraryIcon /> },
			{ key: "ITEMS_TEXT.BANNERS", icon: <CampaignIcon /> },
			// { key: "ITEMS_TEXT.DOCUMENTS", icon: <SourceIcon /> },
			{ key: "ITEMS_TEXT.AMF_METHOD_DOCUMENTATION", icon: <SourceIcon /> },
			{ key: "ITEMS_TEXT.MACHINE_PROVISION", icon: <PrecisionManufacturingIcon /> },
			{ key: "ITEMS_TEXT.PRICE_MARKUPS", icon: <PriceChangeIcon /> },
			{ key: "ITEMS_TEXT.LINE_DISCOUNTS", icon: <PercentIcon /> },
			{ key: "ITEMS_TEXT.PROMOTIONS", icon: <LocalOfferIcon /> },
			// { key: "ITEMS_TEXT.AMFMETHOD_SCHEDULER", icon: <ScheduleIcon /> }, // NEW
			{ key: CHIP_REVIEW_KEY, defaultLabel: "CHIP Governance", icon: <FactCheckOutlinedIcon /> },
		],
	},
	{
		header: TABLE_HEADERS.OPERATIONS, // or TABLE_HEADERS.MODEL_MANAGEMENT
		items: [
			{ key: "ITEMS_TEXT.UPLOAD_MODELS", icon: <FileUploadIcon /> },
		],
	},
		{
			header: TABLE_HEADERS.SUPER_ADMIN,
			items: [
				{ key: "ITEMS_TEXT.ALL_PRODUCTS", icon: <Inventory2Icon /> },
				{ key: "ITEMS_TEXT.ALL_CUSTOMERS", icon: <SupervisorAccountIcon /> },
				{ key: "ITEMS_TEXT.ALL_SITES", icon: <LocalLibraryIcon /> },
				{ key: "ITEMS_TEXT.ALL_MACHINES", icon: <PrecisionManufacturingIcon /> },
				{ key: "ITEMS_TEXT.SCHEDULER", icon: <MoreTimeIcon /> },
			],
		},
	{
		header: TABLE_HEADERS.CONFIGURATION,
		items: [
			{ key: "ITEMS_TEXT.SETTINGS", icon: <ArticleIcon /> },
			{ key: "ITEMS_TEXT.POLICY_CENTER", icon: <GavelIcon /> },
		],
	},
	{
		header: TABLE_HEADERS.PLATFORM_SETTINGS,
		items: [
			{ key: "ITEMS_TEXT.COMPANY_INFORMATION", icon: <StoreIcon /> },
			{ key: "ITEMS_TEXT.THEME", icon: <ContrastIcon /> },
			{ key: "ITEMS_TEXT.VERSION_INFO", icon: <InfoOutlinedIcon /> },
		],
	},
];

const versionTabKey = "ITEMS_TEXT.VERSION_INFO";
const deriveSlug = (key = "") => key.replace(/^ITEMS_TEXT\./, "").toLowerCase();
const navKeys = navItems.flatMap((section) => section.items.map((item) => item.key));
const keyToSlugMap = new Map(navKeys.map((key) => [key, deriveSlug(key)]));
const slugToKeyMap = new Map(navKeys.map((key) => [deriveSlug(key), key]));

const SectionWrapper = ({ title, subtitle, actions, children }) => (
	<Box
		sx={{
			p: 1,
			mb: 2,
		}}
	>
		{title && <HeaderTitle title={title} subtitle={subtitle} actions={actions} />}
		{children}
	</Box>
);

const Admin = () => {
	const [activeIndex, setActiveIndex] = useState("ITEMS_TEXT.USERS");
	const { t } = useTranslation();
	const { user, hasPermission } = useAuth();
	const location = useLocation();
	const navigate = useNavigate();
	const [currentUser, setCurrentUser] = useState(user);
	const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
	const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
	const [versionInfo, setVersionInfo] = useState(null);
	const [versionLoading, setVersionLoading] = useState(false);
	const [versionError, setVersionError] = useState("");
	const { optionKeys } = useOptions();

	const isSuperAdmin = Number(currentUser?.user_id) === 1;
	const canReview = isSuperAdmin || hasPermission('chip_governance.manage_chip_rules');
	const enabledAdminModules = useMemo(() => resolveEnabledAdminModules(optionKeys), [optionKeys]);
	const enabledAdminModuleSet = useMemo(() => new Set(enabledAdminModules), [enabledAdminModules]);

	const requestedKey = useMemo(() => {
		const params = new URLSearchParams(location.search);
		const tabSlug = params.get("tab");
		const legacyPage = params.get("page");
		if (tabSlug) {
			return chipReviewAlias(tabSlug) || slugToKeyMap.get(tabSlug.toLowerCase()) || null;
		}
		if (legacyPage) {
			if (chipReviewAlias(legacyPage)) return CHIP_REVIEW_KEY;
			if (legacyPage.startsWith("ITEMS_TEXT.")) {
				return legacyPage;
			}
			return slugToKeyMap.get(legacyPage.toLowerCase()) || null;
		}
		return null;
	}, [location.search]);
	const isRequestedKeyDisabled =
		!isSuperAdmin &&
		Boolean(requestedKey) &&
		requestedKey !== "ITEMS_TEXT.POLICY_CENTER" &&
		!enabledAdminModuleSet.has(requestedKey);

	useEffect(() => {
		setCurrentUser(user);
	}, [user]);

	// Re-render when window size changes
	useEffect(() => {
		const handleResize = () => {
			setWindowSize({ width: window.innerWidth, height: window.innerHeight });
		};

		window.addEventListener("resize", handleResize);
		// Trigger on mount
		handleResize();

		return () => window.removeEventListener("resize", handleResize);
	}, []);

	// Sidebar toggle handler (manual)
	const toggleSidebar = () => {
		setIsSidebarCollapsed((prev) => !prev);
	};

	// Re-render main Box when sidebar or window size changes
	useEffect(() => {
		// This effect re-runs when size or sidebar changes
	}, [windowSize, isSidebarCollapsed]);

	const settingsPermissionKeys = useMemo(() => [
		'app_settings.manage_settings',
		'app_settings.manage_order_settings',
		'app_settings.manage_inventory_settings',
		'app_settings.manage_site_settings',
		'app_settings.manage_reminder_settings',
		'app_settings.manage_cart_retention',
	], []);

	const canAccess = useCallback((key) => {
		if (isSuperAdmin) {
			return true;
		}
		switch (key) {
			case 'ITEMS_TEXT.USERS':
				return hasPermission('user_management.create_users') || hasPermission('user_management.edit_users') || hasPermission('user_management.delete_users');
			case 'ITEMS_TEXT.ROLES':
				return hasPermission('role_management.create_roles') || hasPermission('role_management.edit_roles') || hasPermission('role_management.delete_roles');
			case 'ITEMS_TEXT.SITES':
				return hasPermission('site_management.edit_sites');
			case 'ITEMS_TEXT.BANNERS':
				return (
					hasPermission('banner_management.view_banners') ||
					hasPermission('banner_management.create_banners') ||
					hasPermission('banner_management.edit_banners') ||
					hasPermission('banner_management.delete_banners')
				);
			case 'ITEMS_TEXT.DOCUMENTS':
				return (
					hasPermission('document_management.create_documents') ||
					hasPermission('document_management.edit_documents') ||
					hasPermission('document_management.delete_documents')
				);
			case 'ITEMS_TEXT.MACHINE_PROVISION':
				return hasPermission('machine_provision_management.provision_machines') || hasPermission('machine_provision_management.edit_provisions') || hasPermission('machine_provision_management.delete_provisions');
			case 'ITEMS_TEXT.AMF_METHOD_DOCUMENTATION':
				return (
					hasPermission('document_management.create_documents') ||
					hasPermission('document_management.edit_documents') ||
					hasPermission('document_management.delete_documents') ||
					hasPermission('document_management.view_documents') ||
					hasPermission('document_management.download_documents')
				);
			case 'ITEMS_TEXT.REMINDERS':
				return hasPermission('order_management.create_order_schedules') || hasPermission('order_management.edit_order_schedules') || hasPermission('order_management.delete_order_schedules');
			case 'ITEMS_TEXT.UPLOAD_MODELS':
				return hasPermission('app_settings.allow_model_uploads');
			case 'ITEMS_TEXT.SETTINGS':
				return settingsPermissionKeys.some((perm) => hasPermission(perm));
			case 'ITEMS_TEXT.COMPANY_INFORMATION':
				return isSuperAdmin;
			case 'ITEMS_TEXT.THEME':
				return isSuperAdmin;
			case 'ITEMS_TEXT.POLICY_CENTER':
				return (
					isSuperAdmin ||
					hasPermission('app_settings.manage_policy_center')
				);
			// case 'ITEMS_TEXT.SCHEDULER':
			// 	return isSuperAdmin && (hasPermission('order_management.create_order_schedules') || hasPermission('order_management.edit_order_schedules') || hasPermission('order_management.delete_order_schedules'));
				case 'ITEMS_TEXT.ALL_PRODUCTS':
				case 'ITEMS_TEXT.ALL_CUSTOMERS':
				case 'ITEMS_TEXT.ALL_SITES':
				case 'ITEMS_TEXT.ALL_MACHINES':
					return isSuperAdmin;
				case CHIP_REVIEW_KEY:
					return canReview;
			case 'ITEMS_TEXT.PRICE_MARKUPS':
				return hasPermission('app_settings.manage_price_markups');
			case 'ITEMS_TEXT.LINE_DISCOUNTS':
				return (
					hasPermission('line_discount_management.view_line_discounts') ||
					hasPermission('line_discount_management.create_line_discounts') ||
					hasPermission('line_discount_management.edit_line_discounts') ||
					hasPermission('line_discount_management.delete_line_discounts')
				);
			case 'ITEMS_TEXT.PROMOTIONS':
				return (
					hasPermission('promotion_management.view_promotions') ||
					hasPermission('promotion_management.create_promotions') ||
					hasPermission('promotion_management.edit_promotions') ||
					hasPermission('promotion_management.delete_promotions')
				);
			case 'ITEMS_TEXT.VERSION_INFO':
				return isSuperAdmin;
			// case 'ITEMS_TEXT.AMFMETHOD_SCHEDULER':
			// 	return true;
			default:
				return false;
		}
	}, [hasPermission, isSuperAdmin, canReview, settingsPermissionKeys]);

	const visibleNavItems = useMemo(() => {
		const enabledSet = enabledAdminModuleSet;
		return navItems
			.map((section) => ({
				...section,
				items: section.items.filter((item) => {
					if (item.key === CHIP_REVIEW_KEY) return canReview && (isSuperAdmin || enabledSet.has(CHIP_REVIEW_KEY));
					if (isSuperAdmin) {
						return true;
					}
					if (item.key === "ITEMS_TEXT.POLICY_CENTER" && isSuperAdmin) return true;
					if (!enabledSet.has(item.key)) return false;
					return true;
				}),
			}))
			.filter((section) => section.items.length > 0);
	}, [enabledAdminModuleSet, isSuperAdmin, canReview]);

	const visibleNavKeySet = useMemo(
		() => new Set(visibleNavItems.flatMap((section) => section.items.map((item) => item.key))),
		[visibleNavItems]
	);

	const getFirstAccessibleKey = useCallback(() => {
		for (const section of visibleNavItems) {
			for (const item of section.items) {
				if (canAccess(item.key)) {
					return item.key;
				}
			}
		}
		return null;
	}, [canAccess, visibleNavItems]);

	const syncQueryParam = useCallback(
		(key) => {
			const slug = keyToSlugMap.get(key);
			const params = new URLSearchParams(location.search);
			const currentTab = params.get("tab");
			if (slug) {
				if (currentTab === slug && !params.get("page")) return;
				params.set("tab", slug);
				params.delete("page");
			} else {
				if (!currentTab && !params.get("page")) return;
				params.delete("tab");
				params.delete("page");
			}
			const queryString = params.toString();
			navigate(`${location.pathname}${queryString ? `?${queryString}` : ""}`, { replace: true });
		},
		[location.pathname, location.search, navigate]
	);

	useEffect(() => {
		if (isRequestedKeyDisabled) {
			setActiveIndex(null);
			return;
		}

		let nextKey = requestedKey;

		if (!nextKey) {
			if (activeIndex && visibleNavKeySet.has(activeIndex) && canAccess(activeIndex)) {
				nextKey = activeIndex;
			} else {
				nextKey = getFirstAccessibleKey();
			}
		}

		if (nextKey && nextKey !== activeIndex) {
			setActiveIndex(nextKey);
		}

		const expectedSlug = keyToSlugMap.get(nextKey || "") || null;
		const params = new URLSearchParams(location.search);
		const tabSlug = params.get("tab");
		const legacyPage = params.get("page");
		const shouldSyncLegacy = Boolean(legacyPage) && expectedSlug;
		const shouldSyncSlug =
			Boolean(expectedSlug) &&
			(tabSlug !== expectedSlug || (legacyPage && expectedSlug));

		if (nextKey && (shouldSyncSlug || shouldSyncLegacy)) {
			syncQueryParam(nextKey);
		}
	}, [
		location.search,
		canAccess,
		activeIndex,
		getFirstAccessibleKey,
		visibleNavKeySet,
		syncQueryParam,
		requestedKey,
		isRequestedKeyDisabled,
	]);

	const translatedNavItems = useMemo(() => {
		return visibleNavItems.map((section) => {
			const translatedHeader = t(section.header);

			const translatedItems = section.items.map((item) => ({
				...item,
				text: item.key,
				translatedText: t(item.key, item.defaultLabel ? { defaultValue: item.defaultLabel } : {}),
			}));

			return {
				...section,
				header: translatedHeader,
				items: translatedItems,
			};
		});
	}, [t, visibleNavItems]);




	const handleNavClick = (key) => {
		setActiveIndex(key);
		syncQueryParam(key);
	};

	const fetchVersionInfo = useCallback(async () => {
		setVersionLoading(true);
		setVersionError("");
		try {
			const response = await getVersionInfo();
			if (response?.status) {
				setVersionInfo(response.data || null);
			} else {
				setVersionError(response?.message || t("UI_TEXT.VERSION_INFO_FETCH_FAILED"));
			}
		} catch (error) {
			setVersionError(t("UI_TEXT.VERSION_INFO_FETCH_FAILED"));
		} finally {
			setVersionLoading(false);
		}
	}, [t]);


	useEffect(() => {
		if (activeIndex === versionTabKey && canAccess(versionTabKey)) {
			if (!versionInfo && !versionLoading) {
				fetchVersionInfo();
			}
		}
	}, [activeIndex, canAccess, fetchVersionInfo, versionInfo, versionLoading]);

	const formatVersionValue = useCallback(
		(value) => (value ? value : t("UI_TEXT.VERSION_INFO_UNKNOWN")),
		[t]
	);

	const versionActions = (
		<Button
			variant="outlined"
			size="small"
			onClick={fetchVersionInfo}
			disabled={versionLoading}
		>
			{t("UI_TEXT.VERSION_INFO_REFRESH_BUTTON")}
		</Button>
	);

	const hasVersionData = Boolean(versionInfo?.frontend || versionInfo?.backend);

	const renderVersionBlock = (title, info = {}) => (
		<Box
			sx={{
				border: "1px solid",
				borderColor: "divider",
				borderRadius: 2,
				p: 2,
				height: "100%",
				display: "flex",
				flexDirection: "column",
				rowGap: 1,
			}}
		>
			<Typography variant="h6" fontWeight={600}>
				{title}
			</Typography>
			<Typography variant="body2" color="text.secondary">
				{t("UI_TEXT.VERSION_INFO_PATH")}: {formatVersionValue(info.repoPath)}
			</Typography>
			<Typography variant="body2">
				<b>{t("UI_TEXT.VERSION_INFO_BRANCH")}:</b> {formatVersionValue(info.branch)}
			</Typography>
			<Typography variant="body2">
				<b>{t("UI_TEXT.VERSION_INFO_COMMIT")}:</b> {formatVersionValue(info.commit)}
			</Typography>
			<Typography variant="body2">
				<b>{t("UI_TEXT.VERSION_INFO_SHORT")}:</b> {formatVersionValue(info.short)}
			</Typography>
			<Typography variant="body2">
				<b>{t("UI_TEXT.VERSION_INFO_TAG")}:</b> {formatVersionValue(info.tag)}
			</Typography>
			<Typography variant="body2">
				<b>{t("UI_TEXT.VERSION_INFO_UPDATED")}:</b> {formatVersionValue(info.lastCommit)}
			</Typography>
			<Typography variant="body2">
				<b>{t("UI_TEXT.VERSION_INFO_STATE")}:</b>{" "}
				{info.isDirty === null
					? formatVersionValue(null)
					: info.isDirty
						? t("UI_TEXT.VERSION_INFO_DIRTY")
						: t("UI_TEXT.VERSION_INFO_CLEAN")}
			</Typography>
		</Box>
	);

	// Simple loading inference: if user object is not ready, show skeleton layout
	const isLoadingAdmin = !user;
	const sidebarWidth = isSidebarCollapsed ? 80 : 300;
	const showSidebarLabels = !isSidebarCollapsed;

	if (isLoadingAdmin) {
		return (
			<Box sx={{ display: 'flex', width: '100vw', minHeight: '100vh' }}>
				<Box sx={{ width: '14%', p: 2 }}>
					<Skeleton variant="rectangular" width={'100%'} height={40} />
					<Divider sx={{ my: 2 }} />
					{[...Array(5)].map((_, i) => (
						<Box key={i} sx={{ mb: 1.5 }}>
							<Skeleton variant="text" width={'60%'} height={18} />
							{[...Array(3)].map((__, j) => (
								<Skeleton key={j} variant="rounded" width={'100%'} height={36} sx={{ mt: 1, borderRadius: '10px' }} />
							))}
						</Box>
					))}
				</Box>
				<Box sx={{ flex: 1, p: 2 }}>
					<Skeleton variant="text" width={260} height={30} />
					<Skeleton variant="rounded" width={'100%'} height={400} sx={{ mt: 2 }} />
				</Box>
			</Box>
		);
	}
	
	return (
		<>

			<Box
				sx={{
					display: "flex",
					width: "100%",
					minHeight: "100vh", // allow growth beyond viewport
					alignItems: "stretch", // ensures children stretch equally in height
				}}
			>
				{/* Sidebar */}
				<Box
					sx={{
						width: sidebarWidth,
						minWidth: sidebarWidth,
						maxWidth: sidebarWidth,
						flexShrink: 0,
						boxShadow: "0 0 20px 0px rgba(0,0,0,0.1)",
						p: 2,
						display: "flex",
						flexDirection: "column",
						transition: "width 0.2s ease",
						flexShrink: 0,
					}}
					data-tour="admin-menu"
				>
					<Box
						sx={{
							flexGrow: 1,
							width: "100%",
							overflowY: "auto",
							overflowX: "hidden",
							scrollbarWidth: "none",
							"&::-webkit-scrollbar": { display: "none" },
						}}
					>
						<Box sx={{ display: 'flex', justifyContent: isSidebarCollapsed ? "center" : 'flex-start', alignItems: 'center', width: '100%', columnGap: 1.5 }}>
							{isSidebarCollapsed ? (<>
								<Tooltip title={t("UI_TEXT.TOOLTIP_EXPAND_SIDE_BAR")} arrow placement="right">
									<Button variant="roundedGreyBgSquareContained" color="primary" onClick={isSidebarCollapsed ? toggleSidebar : null}>
										<AdminPanelSettingsOutlined />
									</Button>
								</Tooltip>
							</>) : (<>
								<Button variant="roundedGreyBgSquareContained" color="primary" onClick={isSidebarCollapsed ? toggleSidebar : null}>
									<AdminPanelSettingsOutlined />
								</Button>
							</>)}

							{showSidebarLabels && (
								<>
									<Box sx={{ flexGrow: 1 }}>
										<Typography variant="h6">{t('UI_TEXT.ADMIN_PANEL')}</Typography>
										<Typography variant="body2" color="text.secondary" fontSize={"12px"}>{t('UI_TEXT.MANAGE_ADMIN_SETTINGS')}</Typography>
									</Box>
									<IconButton size="small" onClick={toggleSidebar} sx={{ ml: showSidebarLabels ? 0 : 'auto' }}>
										{isSidebarCollapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
									</IconButton>
								</>
							)}
						</Box>
						<Divider sx={{ my: 1, mt: 2 }} />
						<List component="nav" aria-labelledby="nested-list-subheader">
							{translatedNavItems.map((section, sectionIndex) => {
								const isSuperAdminSection = section.header === t(TABLE_HEADERS.SUPER_ADMIN);
								const visibleItems = section.items.filter((item) => {
									if (isSuperAdminSection) return isSuperAdmin && canAccess(item.key);
									return canAccess(item.key);
								});
								if (visibleItems.length === 0) return null; // Hide section if no items are visible
								return (
									<React.Fragment key={section.header}>
										{showSidebarLabels && (
											<Box
												sx={{
													display: 'flex',
													justifyContent: 'flex-start',
													alignItems: 'center',
													columnGap: 2,
													mt: sectionIndex !== 0 ? 2 : 0
												}} >
												<Typography variant="body1" fontSize={"11px"} fontWeight={"bold"} color={"text.secondary"}>{section.header}</Typography>
											</Box>
										)}

										{visibleItems.map((item) => (
											<Tooltip title={item.translatedText} key={item.key} placement="right" arrow>
												<ListItemButton
													sx={{
														my: 0.5,
														borderRadius: "10px",
														justifyContent: showSidebarLabels ? 'flex-start' : 'center',
													}}
													selected={activeIndex === item.key}
													onClick={() => handleNavClick(item.key)}>
													<Box
														sx={{
															display: 'flex',
															justifyContent: 'flex-start',
															alignItems: 'center',
															columnGap: showSidebarLabels ? 2 : 0,
															width: "100%",
															py: 0.5,
															color: activeIndex === item?.key ? 'primary.main' : '#252529',
															'& svg': {
																color: activeIndex === item?.key ? 'primary.main' : '#252529',
															},
														}}
													>
														{item.icon}
														{showSidebarLabels && (
															<Typography variant="body1" fontSize={"13px"}>
																{item.translatedText}
															</Typography>
														)}
													</Box>


												</ListItemButton>
											</Tooltip>
										))}
									</React.Fragment>
								);
							})}
						</List>
					</Box>
				</Box>

				{/* Main content */}
				<Box
					// key={`${windowSize.width}-${windowSize.height}-${isSidebarCollapsed}`}
					sx={{
						flex: 1,
							minWidth: 0,
							px: 2,
							py: 1,
							height: "auto", // allow height to grow
					}}
					data-tour="key-console-actions"
				>
					{isRequestedKeyDisabled && <PageNotFound />}
					{!isRequestedKeyDisabled && activeIndex === "ITEMS_TEXT.USERS" && (
						<SectionWrapper title={t('UI_TEXT.MANAGE_USERS')} subtitle={t('UI_TEXT.ADMIN_CAN_EDIT_REMOVE_USERS')}>
							{canAccess('ITEMS_TEXT.USERS') ? <ManageUsers showHeader={false} /> : <NotAuthorized />}
						</SectionWrapper>
					)}
					{!isRequestedKeyDisabled && activeIndex === "ITEMS_TEXT.ROLES" && (
						<SectionWrapper title={t('ITEMS_TEXT.ROLES')} subtitle={t('UI_TEXT.MANAGE_USER_ROLES')}>
							{canAccess('ITEMS_TEXT.ROLES') ? <ManageRoles showHeader={false} /> : <NotAuthorized />}
						</SectionWrapper>
					)}
					{!isRequestedKeyDisabled && activeIndex === "ITEMS_TEXT.SITES" && (
						<SectionWrapper title={t('UI_TEXT.MANAGE_SITES')} subtitle={t('UI_TEXT.ADMIN_CAN_EDIT_REMOVE_SITES')}>
							{canAccess('ITEMS_TEXT.SITES') ? <ManageSites /> : <NotAuthorized />}
						</SectionWrapper>
					)}
					{!isRequestedKeyDisabled && activeIndex === "ITEMS_TEXT.BANNERS" && (
						<SectionWrapper title={t('ITEMS_TEXT.BANNERS', { defaultValue: 'Banners' })} subtitle={t('UI_TEXT.MANAGE_BANNERS_SUBTITLE', { defaultValue: 'Admin can create and manage messages shown across the portal for selected regions.' })}>
							{canAccess('ITEMS_TEXT.BANNERS') ? <ManageBanners /> : <NotAuthorized />}
						</SectionWrapper>
					)}
					{!isRequestedKeyDisabled && activeIndex === "ITEMS_TEXT.DOCUMENTS" && (
						<SectionWrapper title={t('UI_TEXT.MANAGE_DOCUMENTS')} subtitle={t('UI_TEXT.ADMIN_CAN_ADD_REMOVE_DOCUMENTS')}>
							{canAccess('ITEMS_TEXT.DOCUMENTS') ? <ManageDocuments /> : <NotAuthorized />}
						</SectionWrapper>
					)} 
					{activeIndex === "ITEMS_TEXT.AMF_METHOD_DOCUMENTATION" && (
						<SectionWrapper
							title={t('UI_TEXT.AMF_METHOD_DOCUMENTATION')}
							subtitle={t('UI_TEXT.ADMIN_VIEW_DOWNLOAD_DOCUMENTS')}
							actions={
								<Box sx={{ mb: 0, display: "flex", justifyContent: "flex-end" }}>
								</Box>
							}
						>
							{canAccess('ITEMS_TEXT.AMF_METHOD_DOCUMENTATION') ? (
								<AdminDocumentation />
							) : (
								<NotAuthorized />
							)}
						</SectionWrapper>
					)}
					{activeIndex === "ITEMS_TEXT.MACHINE_PROVISION" && (
						<SectionWrapper>
							{canAccess('ITEMS_TEXT.MACHINE_PROVISION') ? <ManageMachineProvision /> : <NotAuthorized />}
						</SectionWrapper>
					)}
					{!isRequestedKeyDisabled && activeIndex === "ITEMS_TEXT.PRICE_MARKUPS" && (
						<SectionWrapper title={t('ITEMS_TEXT.PRICE_MARKUPS')} subtitle={t('UI_TEXT.MANAGE_PRICE_MARKUP')}>
							{canAccess('ITEMS_TEXT.PRICE_MARKUPS') ? <PriceMarkup /> : <NotAuthorized />}
						</SectionWrapper>
					)}
					{!isRequestedKeyDisabled && activeIndex === "ITEMS_TEXT.LINE_DISCOUNTS" && (
						<SectionWrapper title={t('ITEMS_TEXT.LINE_DISCOUNTS', { defaultValue: 'Line Discounts' })} subtitle={t('UI_TEXT.MANAGE_LINE_DISCOUNTS', { defaultValue: 'Admin can manage line-level discounts by Bakery and Bakery Location.' })}>
							{canAccess('ITEMS_TEXT.LINE_DISCOUNTS') ? <LineDiscounts /> : <NotAuthorized />}
						</SectionWrapper>
					)}
					{!isRequestedKeyDisabled && activeIndex === "ITEMS_TEXT.PROMOTIONS" && (
						<SectionWrapper title={t('ITEMS_TEXT.PROMOTIONS')} subtitle={t('UI_TEXT.MANAGE_PROMOTIONS')}>
							{canAccess('ITEMS_TEXT.PROMOTIONS') ? <PromotionCodes /> : <NotAuthorized />}
						</SectionWrapper>
					)}
					{!isRequestedKeyDisabled && activeIndex === "ITEMS_TEXT.UPLOAD_MODELS" && (
						<SectionWrapper>
							{canAccess('ITEMS_TEXT.UPLOAD_MODELS') ? <ModelUpload /> : <NotAuthorized />}
						</SectionWrapper>
					)}
					{!isRequestedKeyDisabled && activeIndex === "ITEMS_TEXT.SCHEDULER" && (
						<SectionWrapper title={t('UI_TEXT.MANAGE_CRON_JOBS')} subtitle={t('UI_TEXT.ADMIN_CAN_MANAGE_CRON_SCHEDULE_NEW_JOBS')}>
							{canAccess('ITEMS_TEXT.SCHEDULER') ? <ManageCronJobs /> : <NotAuthorized />}
						</SectionWrapper>
					)}
					{activeIndex === "ITEMS_TEXT.ALL_CUSTOMERS" && (
						<SectionWrapper title={t('UI_TEXT.MANAGE_ALL_CUSTOMERS')} subtitle={t('UI_TEXT.ADMINS_CAN_CREATE_EDIT_DELETE_RECORDS')}>
							{canAccess('ITEMS_TEXT.ALL_CUSTOMERS') ? <ManageCustomersMaster /> : <NotAuthorized />}
						</SectionWrapper>
					)}
					{!isRequestedKeyDisabled && activeIndex === "ITEMS_TEXT.ALL_SITES" && (
						<SectionWrapper title={t('UI_TEXT.MANAGE_ALL_SITES')} subtitle={t('UI_TEXT.ADMIN_CAN_CREATE_EDIT_DELETE_RECORDS_SITES')}>
							{canAccess('ITEMS_TEXT.ALL_SITES') ? <ManageSitesMaster /> : <NotAuthorized />}
						</SectionWrapper>
					)}
					{!isRequestedKeyDisabled && activeIndex === "ITEMS_TEXT.ALL_MACHINES" && (
						<SectionWrapper title={t('UI_TEXT.MANAGE_ALL_MACHINES')} subtitle={t('UI_TEXT.ADMIN_CAN_ALL_RECORDS_MASTERS')}>
							{canAccess('ITEMS_TEXT.ALL_MACHINES') ? <ManageMachinesMaster /> : <NotAuthorized />}
						</SectionWrapper>
					)}
					{!isRequestedKeyDisabled && activeIndex === CHIP_REVIEW_KEY && (
						<SectionWrapper title={t(CHIP_REVIEW_KEY, { defaultValue: 'CHIP Governance' })}
							subtitle="Review feedback from all users and bakeries, generate selected suggestions, and approve CHIP guidance and recommendations.">
							{canReview ? <ChipLearningRules /> : <NotAuthorized />}
						</SectionWrapper>
					)}
						{!isRequestedKeyDisabled && activeIndex === "ITEMS_TEXT.ALL_PRODUCTS" && (
							<SectionWrapper title={t('UI_TEXT.MANAGE_ALL_PRODUCTS')} subtitle={t('UI_TEXT.ADMIN_CAN_CREATE_EDIT_DELETE_RECORD_FROM_PRODUCT')}>
								{canAccess('ITEMS_TEXT.ALL_PRODUCTS') ? <ManageProductsMaster /> : <NotAuthorized />}
							</SectionWrapper>
						)}
						{!isRequestedKeyDisabled && activeIndex === "ITEMS_TEXT.SETTINGS" && (
							<SectionWrapper title={t('UI_TEXT.SETTINGS')} subtitle={t('UI_TEXT.MANAGE_ALL_APPLICATION_DEFAULT_VALUES')}>
								{canAccess('ITEMS_TEXT.SETTINGS') ? <DefaultValues /> : <NotAuthorized />}
							</SectionWrapper>
						)}
					{!isRequestedKeyDisabled && activeIndex === "ITEMS_TEXT.COMPANY_INFORMATION" && (
						<SectionWrapper title={t('UI_TEXT.COMPANY_INFORMATIONS')} subtitle={t('UI_TEXT.MANAGE_ALL_THE_COMPANY_INFORMATION')}>
							{canAccess('ITEMS_TEXT.COMPANY_INFORMATION') ? <CompanyBranding /> : <NotAuthorized />}
						</SectionWrapper>
					)}
					{!isRequestedKeyDisabled && activeIndex === "ITEMS_TEXT.THEME" && (
						<SectionWrapper title={t('UI_TEXT.THEMES')} subtitle={t('UI_TEXT.MANAGE_THEME_APPLICATION')}>
							{canAccess('ITEMS_TEXT.THEME') ? <Settings /> : <NotAuthorized />}
						</SectionWrapper>
					)}
					{!isRequestedKeyDisabled && activeIndex === "ITEMS_TEXT.POLICY_CENTER" && (
						<SectionWrapper title={t('LABELS.POLICY_CENTER')} subtitle={t("ITEMS_TEXT.POLICY_CENTER_SUBTITLE") ||
							"Manage footer links, EULA versions, and default support policies from a single place."}>
							{canAccess('ITEMS_TEXT.POLICY_CENTER') ? <PolicyCenter /> : <NotAuthorized />}
						</SectionWrapper>
					)}
					{!isRequestedKeyDisabled && activeIndex === versionTabKey && (
						<SectionWrapper
							title={t('UI_TEXT.VERSION_INFO_TITLE')}
							subtitle={t('UI_TEXT.VERSION_INFO_SUBTITLE')}
							actions={versionActions}
						>
							{canAccess(versionTabKey) ? (
								versionLoading ? (
									<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
										<CircularProgress />
									</Box>
								) : versionError ? (
									<Alert severity="error">{versionError}</Alert>
								) : hasVersionData ? (
									<Grid container spacing={2}>
										<Grid item xs={12} md={6}>
											{renderVersionBlock(t('LABELS.PORTAL_FRONTEND'), versionInfo?.frontend)}
										</Grid>
										<Grid item xs={12} md={6}>
											{renderVersionBlock(t('LABELS.API_BACKEND'), versionInfo?.backend)}
										</Grid>
									</Grid>
								) : (
									<Alert severity="info">{t('UI_TEXT.VERSION_INFO_NO_DATA')}</Alert>
								)
							) : (
								<NotAuthorized />
							)}
						</SectionWrapper>
					)}
				</Box>
			</Box>


		</>
	);
}

export default Admin;
